import { Box, Button, Typography } from "@mui/material";
import { COLORS, primaryContainedButtonSx } from "assets";
import { StatusPill } from "components";
import { useContextController } from "context";
import { incidentsApi } from "incidents/api";
import { countdownBadge, typeLabel } from "incidents/display";
import { Incident } from "incidents/types";
import { canOpenConfirmationQueue } from "incidents/roles";
import { formatWhen } from "incidents/time";
import PrivateLayout from "layouts/PrivateLayout";
import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { primaryButtonSx, SectionTitle, usePhrase } from "./common";

export default function ConfirmationsPage({ preview = false }: { preview?: boolean }) {
  const phrase = usePhrase();
  const locale = phrase("en", "fr") === "fr" ? "fr" : "en";
  const [{ userData }] = useContextController();
  const [params] = useSearchParams();
  const previewRole = params.get("as") === "supervisor" || preview ? "supervisor" : undefined;
  const allowed = canOpenConfirmationQueue(userData, preview ? previewRole : undefined);
  const [rows, setRows] = useState<Incident[]>([]);
  const [error, setError] = useState<string | null>(null);
  const base = preview ? "/dev/incidents" : "/incidents";

  useEffect(() => {
    incidentsApi
      .list({ status: "false_alert_pending_confirm" })
      .then(setRows)
      .catch((err: Error) => setError(err.message));
  }, []);

  const body = (
    <Box>
      <Button component={Link} to={base} sx={{ ...primaryButtonSx, mb: 2 }}>
        {phrase("Back to incidents", "Retour aux incidents")}
      </Button>
      <Typography component="h1" sx={{ fontSize: 28, fontWeight: 700 }}>
        {phrase("Dismissal confirmations", "Confirmations de rejet")}
      </Typography>
      <SectionTitle
        title={phrase(
          "Unsafe interaction dismissals need a second person",
          "Les rejets d'interaction dangereuse exigent une deuxième personne"
        )}
        hint={phrase(
          "You can confirm or reject. Rejecting opens the incident. You cannot block someone else from escalating. Marking an alert false does not remove a duty to report.",
          "Vous pouvez confirmer ou rejeter. Rejeter ouvre l'incident. Vous ne pouvez pas empêcher une autre personne d'escalader. Marquer une alerte comme fausse n'enlève pas le devoir de signaler."
        )}
      />
      {!allowed ? (
        <Typography role="alert">
          {phrase(
            "A manager or director of care opens this queue.",
            "Une gestionnaire ou la directrice des soins ouvre cette liste."
          )}
        </Typography>
      ) : null}
      {error ? (
        <Typography role="alert" color="error">
          {error}
        </Typography>
      ) : null}
      {allowed && !rows.length ? (
        <Typography>{phrase("Nothing is waiting.", "Rien n'est en attente.")}</Typography>
      ) : null}
      <Box component="ul" sx={{ listStyle: "none", p: 0, display: "grid", gap: 1.5 }}>
        {allowed
          ? rows.map((incident) => {
              const badge = countdownBadge(incident, new Date(), locale);
              return (
                <Box
                  component="li"
                  key={incident.id}
                  sx={{
                    p: 2,
                    border: `1px solid ${COLORS.surface.border}`,
                    borderRadius: 2,
                    bgcolor: COLORS.surface.card
                  }}
                >
                  <Typography sx={{ fontWeight: 700 }}>{incident.id}</Typography>
                  <Typography>
                    {incident.facility_name} · {typeLabel(incident.type, locale)} · {phrase("Room", "Chambre")}{" "}
                    {incident.room || "—"}
                  </Typography>
                  <Typography>
                    {phrase("Marked by", "Marqué par")} {incident.false_alert?.dismissed_by_name} ·{" "}
                    {formatWhen(incident.false_alert?.dismissed_at, locale === "fr" ? "fr-CA" : "en-CA")}
                  </Typography>
                  <Typography>{incident.false_alert?.note}</Typography>
                  <Box mt={1}>
                    <StatusPill label={badge.label} tone={badge.tone === "error" ? "error" : "warning"} />
                  </Box>
                  <Button
                    component={Link}
                    to={`${base}/${incident.id}${preview ? "?as=supervisor" : ""}`}
                    sx={{ ...primaryButtonSx, ...primaryContainedButtonSx, mt: 1 }}
                  >
                    {phrase("Review dismissal", "Réviser le rejet")}
                  </Button>
                </Box>
              );
            })
          : null}
      </Box>
    </Box>
  );

  if (preview) return <Box sx={{ p: 2, bgcolor: COLORS.surface.page, minHeight: "100vh" }}>{body}</Box>;
  return <PrivateLayout canAccess={() => true}>{body}</PrivateLayout>;
}
